import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import TitleBox from 'src/components/title-box/title-box';
import { i18nKey } from 'src/locales/i18n';
import styles from './financial-details.module.less';
import { Col, Form, Input, Row, Typography } from 'antd';
import CurrencyInput from 'src/components/input-with-currency/input-with-currency';
import { noop } from 'src/helpers/window';
import { IIndividualBorrowerOnboardingStore } from 'src/store/user/individual-borrower-onboarding.store';
import useStore from 'src/hooks/useStore';
import ShadowBox from 'src/components/shadow-box/shadow-box';
import CountryDistrictSection from 'src/components/country-district-section/country-district-section';
import { observer } from 'mobx-react-lite';
import { IFinancialRealEstate } from 'src/interfaces/accounts/account';
import { safeSummaryNumber } from 'src/helpers/string.utils';
import { ZIPCODE_REGEX } from 'src/constants/validation';

const FinacialDetails = () => {
	const [t] = useTranslation();
	const [form] = Form.useForm();

	const onboardStore: IIndividualBorrowerOnboardingStore = useStore('individualBorrowerOnboardingStore');

	const handleRealEstateChange = () => {
		const mainRealEstate: IFinancialRealEstate = form.getFieldValue('mainRealEstate') || {};
		const additionRealEstate: IFinancialRealEstate[] = form.getFieldValue('financialRealEstates') || [];
		const marketValue = [mainRealEstate.marketValue || 0];
		const mortgage = [mainRealEstate.mortgageAmount || 0];
		const outstanding = [mainRealEstate.outstandingLoanAmount || 0];
		additionRealEstate.forEach((addr) => {
			if (addr) {
				marketValue.push(addr.marketValue || 0);
				mortgage.push(addr.mortgageAmount || 0);
				outstanding.push(addr.outstandingLoanAmount || 0);
			}
		});

		form.setFieldValue('totalValue', safeSummaryNumber(...marketValue));
		form.setFieldValue('totalMortgage', safeSummaryNumber(...mortgage));
		form.setFieldValue('totalOutstandingLoanAmt', safeSummaryNumber(...outstanding));
	};

	useEffect(() => {
		handleRealEstateChange();

		if (!form.isFieldsTouched()) {
			form.setFieldsValue({
				...onboardStore.borrowerFinancialDetail,
				mainRealEstate: onboardStore.mainRealEstate
			});
		}
	}, [onboardStore.borrowerFinancialDetail, onboardStore.mainRealEstate]);

	return (
		<>
			<Form
				form={form}
				disabled
				layout="vertical"
				initialValues={{
					...onboardStore.borrowerFinancialDetail,
					mainRealEstate: onboardStore.mainRealEstate
				}}>
				<TitleBox>
					<span>{t(i18nKey.mortgageDeal.requestForm.borrowerRequest.PersonalAssetsAndLiabilities)}</span>
				</TitleBox>
				<div className={styles.subForm}>
					<Row>
						<Col span={24}>
							<Typography.Title level={3}>
								{t(i18nKey.onboarding.request.financialDetails.sourceOfIncome)}
							</Typography.Title>
						</Col>
						<Col span={24} className={styles.dividerLine}></Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.wages)}`}>
								<CurrencyInput
									disabled
									name={['financialYearlyIncome', 'wages']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.interestDividends)}`}>
								<CurrencyInput
									disabled
									name={['financialYearlyIncome', 'interestDividends']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.spouse)}`}>
								<CurrencyInput
									disabled
									name={['financialYearlyIncome', 'spouse']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.rentalIncome)}`}>
								<CurrencyInput
									disabled
									name={['financialYearlyIncome', 'rentalIncome']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.investmentIncome)}`}>
								<CurrencyInput
									disabled
									name={['financialYearlyIncome', 'investmentIncome']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.others)}`}>
								<CurrencyInput
									disabled
									name={['financialYearlyIncome', 'others']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={`${t(i18nKey.onboarding.request.financialDetails.totalAnnualIncome)}`}>
								<CurrencyInput
									name={['financialYearlyIncome', 'totalAnnualIncome']}
									disabled
									currency={onboardStore.onboardingCurrency}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<Typography.Title level={3}>{t(i18nKey.onboarding.request.financialDetails.yearly)}</Typography.Title>
						</Col>
						<Col span={24} className={styles.dividerLine}></Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item
								required={true}
								label={`${t(i18nKey.onboarding.request.financialDetails.residentialInvestmentMortgage)}`}>
								<CurrencyInput
									disabled
									name={['financialRegularExpenditure', 'residentialInvestmentMortgage']}
									currency={onboardStore.onboardingCurrency}
									onDataChange={async () => {
										try {
											await form.validateFields([
												['mainRealEstate', 'marketValue'],
												['mainRealEstate', 'mortgageAmount'],
												['mainRealEstate', 'outstandingLoanAmount']
											]);
										} catch {
											noop();
										}
									}}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.annuityRent)}`}>
								<CurrencyInput
									disabled
									name={['financialRegularExpenditure', 'annuityRent']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.personalFinance)}`}>
								<CurrencyInput
									disabled
									name={['financialRegularExpenditure', 'personalFinanceInstallment']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.household)}`}>
								<CurrencyInput
									disabled
									name={['financialRegularExpenditure', 'household']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.others)}`}>
								<CurrencyInput
									disabled
									name={['financialRegularExpenditure', 'others']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={t(i18nKey.onboarding.request.financialDetails.totalYearsExp)}>
								<CurrencyInput
									name={['financialRegularExpenditure', 'totalYearlyExpenditures']}
									disabled
									currency={onboardStore.onboardingCurrency}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={`${t(i18nKey.onboarding.request.financialDetails.yearlyDisposableIncome)}`}>
								<CurrencyInput
									name={['financialRegularExpenditure', 'yearlyDisposableIncome']}
									disabled
									currency={onboardStore.onboardingCurrency}
								/>
							</Form.Item>
						</Col>
					</Row>
				</div>
				<TitleBox>
					<span>{t(i18nKey.onboarding.request.financialDetails.realEstateAsset)}</span>
				</TitleBox>
				<ShadowBox>
					<div className="common-padding">
						<Row gutter={[32, 16]}>
							<Col span={24}>
								<Row>
									<Col span={24} className={styles.titleName}>
										{t(i18nKey.onboarding.request.financialDetails.address)}
									</Col>
								</Row>
								<Row>
									<Col span={24}>
										<div className={styles.dividerLine}></div>
									</Col>
								</Row>

								<Row gutter={[32, 16]}>
									<Col span={24}>
										<Form.Item
											name={['mainRealEstate', 'address']}
											label={`${t(i18nKey.dashboard.accountInformation.adrLine)}`}
											required={true}
											rules={[
												{
													required: true,
													message: `${t(i18nKey.onboarding.validation.required)}`
												}
											]}>
											<Input />
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={[32, 16]}>
									<Col span={24}>
										<Form.Item
											name={['mainRealEstate', 'street']}
											label={`${t(i18nKey.dashboard.accountInformation.street)}`}
											required={true}
											rules={[
												{
													required: true,
													message: `${t(i18nKey.onboarding.validation.required)}`
												}
											]}>
											<Input />
										</Form.Item>
									</Col>
								</Row>
								<CountryDistrictSection
									countryName={['mainRealEstate', 'country']}
									countryLabel={`${t(i18nKey.dashboard.accountInformation.country)}`}
									countryRules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
									districtName={['mainRealEstate', 'district']}
									districtLabel={`${t(i18nKey.dashboard.accountInformation.district)}`}
									districtRules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
									requireCountry
									requireDistrict
								/>

								<Row gutter={[32, 16]}>
									<Col span={24}>
										<Form.Item
											name={['mainRealEstate', 'zipCode']}
											label={`${t(i18nKey.dashboard.accountInformation.zipCode)}`}
											required={true}
											rules={[
												{
													required: true,
													message: `${t(i18nKey.onboarding.validation.required)}`
												},
												{
													pattern: ZIPCODE_REGEX,
													message: `${t(i18nKey.onboarding.validation.zipCode)}`
												}
											]}>
											<Input />
										</Form.Item>
									</Col>
								</Row>
							</Col>
						</Row>
						<Row gutter={[32, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.marketValue)}`}>
									<CurrencyInput
										disabled
										name={['mainRealEstate', 'marketValue']}
										currency={onboardStore.onboardingCurrency}
										rules={[
											({ getFieldValue }) => ({
												validator(_, value) {
													const residentialValue = getFieldValue([
														'financialRegularExpenditure',
														'residentialInvestmentMortgage'
													]);

													if (!value && residentialValue && residentialValue != '0') {
														return Promise.reject(new Error(`${t(i18nKey.onboarding.validation.required)}`));
													}
													return Promise.resolve();
												}
											})
										]}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.mortgageAmt)}`}>
									<CurrencyInput
										disabled
										name={['mainRealEstate', 'mortgageAmount']}
										currency={onboardStore.onboardingCurrency}
										rules={[
											({ getFieldValue }) => ({
												validator(_, value) {
													const residentialValue = getFieldValue([
														'financialRegularExpenditure',
														'residentialInvestmentMortgage'
													]);

													if (!value && residentialValue && residentialValue != '0') {
														return Promise.reject(new Error(`${t(i18nKey.onboarding.validation.required)}`));
													}
													return Promise.resolve();
												}
											})
										]}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={[32, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.outstandingAmt)}`}>
									<CurrencyInput
										disabled
										name={['mainRealEstate', 'outstandingLoanAmount']}
										currency={onboardStore.onboardingCurrency}
										rules={[
											({ getFieldValue }) => ({
												validator(_, value) {
													const residentialValue = getFieldValue([
														'financialRegularExpenditure',
														'residentialInvestmentMortgage'
													]);

													if (!value && residentialValue && residentialValue != '0') {
														return Promise.reject(new Error(`${t(i18nKey.onboarding.validation.required)}`));
													}
													return Promise.resolve();
												}
											})
										]}
									/>
								</Form.Item>
							</Col>
						</Row>
					</div>
				</ShadowBox>
				<Form.List name="financialRealEstates">
					{(fields) => (
						<>
							<div className={styles.dividerAddrLine}></div>
							{fields.map((f, idx) => (
								<ShadowBox key={f.key}>
									<div className="common-padding">
										<Row gutter={[32, 16]}>
											<Col span={24}>
												<Row>
													<Col span={24} className={styles.titleName}>
														{t(i18nKey.onboarding.request.financialDetails.address)}
													</Col>
												</Row>
												<Row>
													<Col span={24}>
														<div className={styles.dividerLine}></div>
													</Col>
												</Row>

												<Row gutter={[32, 16]}>
													<Col span={24}>
														<Form.Item
															name={[idx, 'address']}
															label={`${t(i18nKey.dashboard.accountInformation.adrLine)}`}
															required={false}
															rules={[
																{
																	required: true,
																	message: `${t(i18nKey.onboarding.validation.required)}`
																}
															]}>
															<Input />
														</Form.Item>
													</Col>
												</Row>
												<Row gutter={[32, 16]}>
													<Col span={24}>
														<Form.Item
															name={[idx, 'street']}
															label={`${t(i18nKey.dashboard.accountInformation.street)}`}
															required={false}
															rules={[
																{
																	required: true,
																	message: `${t(i18nKey.onboarding.validation.required)}`
																}
															]}>
															<Input />
														</Form.Item>
													</Col>
												</Row>
												<CountryDistrictSection
													countryName={[idx, 'country']}
													countryLabel={`${t(i18nKey.dashboard.accountInformation.country)}`}
													countryRules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}
													districtName={[idx, 'district']}
													districtLabel={`${t(i18nKey.dashboard.accountInformation.district)}`}
													districtRules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}
												/>

												<Row gutter={[32, 16]}>
													<Col span={24}>
														<Form.Item
															name={[idx, 'zipCode']}
															label={`${t(i18nKey.dashboard.accountInformation.zipCode)}`}
															required={false}
															rules={[
																{
																	required: true,
																	message: `${t(i18nKey.onboarding.validation.required)}`
																},
																{
																	pattern: ZIPCODE_REGEX,
																	message: `${t(i18nKey.onboarding.validation.zipCode)}`
																}
															]}>
															<Input />
														</Form.Item>
													</Col>
												</Row>
											</Col>
										</Row>
										<Row gutter={[32, 16]}>
											<Col xs={24} sm={24} md={24} lg={12}>
												<Form.Item label={t(i18nKey.onboarding.request.financialDetails.marketValue)}>
													<CurrencyInput
														disabled
														name={[idx, 'marketValue']}
														currency={onboardStore.onboardingCurrency}
														rules={[
															{
																required: true,
																message: `${t(i18nKey.onboarding.validation.required)}`
															}
														]}
													/>
												</Form.Item>
											</Col>
											<Col xs={24} sm={24} md={24} lg={12}>
												<Form.Item label={t(i18nKey.onboarding.request.financialDetails.mortgageAmt)}>
													<CurrencyInput
														disabled
														name={[idx, 'mortgageAmount']}
														currency={onboardStore.onboardingCurrency}
														rules={[
															{
																required: true,
																message: `${t(i18nKey.onboarding.validation.required)}`
															}
														]}
													/>
												</Form.Item>
											</Col>
										</Row>
										<Row gutter={[32, 16]}>
											<Col xs={24} sm={24} md={24} lg={12}>
												<Form.Item label={t(i18nKey.onboarding.request.financialDetails.outstandingAmt)}>
													<CurrencyInput
														disabled
														name={[idx, 'outstandingLoanAmount']}
														currency={onboardStore.onboardingCurrency}
														rules={[
															{
																required: true,
																message: `${t(i18nKey.onboarding.validation.required)}`
															}
														]}
													/>
												</Form.Item>
											</Col>
										</Row>
									</div>
								</ShadowBox>
							))}
						</>
					)}
				</Form.List>
				<div className={styles.subForm}>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={t(i18nKey.onboarding.request.financialDetails.totalValue)}>
								<CurrencyInput name="totalValue" disabled currency={onboardStore.onboardingCurrency} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={t(i18nKey.onboarding.request.financialDetails.totalMortgage)}>
								<CurrencyInput name="totalMortgage" disabled currency={onboardStore.onboardingCurrency} />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={t(i18nKey.onboarding.request.financialDetails.totalOutstandingLoanAmt)}>
								<CurrencyInput name="totalOutstandingLoanAmt" disabled currency={onboardStore.onboardingCurrency} />
							</Form.Item>
						</Col>
					</Row>
				</div>
				<TitleBox>
					<span>{t(i18nKey.onboarding.request.financialDetails.personalBalanceSheet)}</span>
				</TitleBox>
				<div className={styles.subForm}>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.deposits)}`}>
								<CurrencyInput
									disabled
									name={['personalBalanceSheet', 'deposits']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.bonds)}`}>
								<CurrencyInput
									disabled
									name={['personalBalanceSheet', 'bonds']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.pensions)}`}>
								<CurrencyInput
									disabled
									name={['personalBalanceSheet', 'pensions']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.stockFunds)}`}>
								<CurrencyInput
									disabled
									name={['personalBalanceSheet', 'stockFunds']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.others)}`}>
								<CurrencyInput
									disabled
									name={['personalBalanceSheet', 'personalBalanceSheetOther']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={t(i18nKey.onboarding.request.financialDetails.realEstates)}>
								<CurrencyInput
									name={['personalBalanceSheet', 'realEstates']}
									disabled
									currency={onboardStore.onboardingCurrency}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={t(i18nKey.onboarding.request.financialDetails.totalAssets)}>
								<CurrencyInput
									name={['personalBalanceSheet', 'totalAssets']}
									disabled
									currency={onboardStore.onboardingCurrency}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.personalLoans)}`}>
								<CurrencyInput
									disabled
									name={['personalBalanceSheet', 'personalLoans']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.carLoans)}`}>
								<CurrencyInput
									disabled
									name={['personalBalanceSheet', 'carLoans']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.taxLoans)}`}>
								<CurrencyInput
									disabled
									name={['personalBalanceSheet', 'taxLoans']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item required={true} label={`${t(i18nKey.onboarding.request.financialDetails.otherLoans)}`}>
								<CurrencyInput
									disabled
									name={['personalBalanceSheet', 'otherLoans']}
									currency={onboardStore.onboardingCurrency}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={t(i18nKey.onboarding.request.financialDetails.mortgage)}>
								<CurrencyInput
									name={['personalBalanceSheet', 'personalBalanceSheetMortgage']}
									disabled
									currency={onboardStore.onboardingCurrency}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={t(i18nKey.onboarding.request.financialDetails.totalLiabilities)}>
								<CurrencyInput
									name={['personalBalanceSheet', 'totalLiabilities']}
									disabled
									currency={onboardStore.onboardingCurrency}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[32, 16]}>
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={t(i18nKey.onboarding.request.financialDetails.netAssess)}>
								<CurrencyInput
									name={['personalBalanceSheet', 'netAssets']}
									disabled
									currency={onboardStore.onboardingCurrency}
								/>
							</Form.Item>
						</Col>
					</Row>
				</div>
			</Form>
		</>
	);
};

export default observer(FinacialDetails);
